import color from "color";
import ButtonRow from "components/ButtonRow";
import KitCloseButton from "components/CloseButton";
import Icon from "components/Icon/Icon";
import { StyledScrollBars } from "components/ScrollableContent/ScrollableContent";
import { fadeIn } from "react-animations";
import styled, { keyframes } from "styled-components";
import { buildDotGridSvg } from "styles/styles";

export const PANEL_WIDTH = "420px";
export const PANEL_SLIDE_MS = 300;

interface WrapperProps {
  $isOpen: boolean;
}

// Slides in over the page under its soft shadow: like the popovers, it floats
// over content, and a hairline alone read as one more table rule (docs
// LANDING_AZURE_PAPER.md §12).
export const Wrapper = styled.div<WrapperProps>`
  user-select: text;
  z-index: 3;
  position: fixed;
  max-width: ${PANEL_WIDTH};
  width: 100%;
  height: 100%;
  right: 0;
  background-color: ${({ theme }) => theme.surfacePanel};
  background-image: ${({ theme }) => buildDotGridSvg(theme.surfaceTextureDot)};
  background-repeat: repeat;
  background-size: 5px 5px;
  transform: translateX(
    ${({ $isOpen }) => ($isOpen ? "0" : `calc(${PANEL_WIDTH} + 20px)`)}
  );
  box-shadow: ${({ theme }) => theme.panelShadow};
  transition: transform ${PANEL_SLIDE_MS}ms
    ${({ $isOpen }) => ($isOpen ? "ease-out" : "ease-in")};
  will-change: transform;

  ${({ $isOpen, theme }) => theme.xs`
    max-width: 100vw;
    box-shadow: none;
    transform: translateX(${$isOpen ? "0" : "100vw"});
    ${!$isOpen ? "transition-duration: 150ms;" : ""}
  `};
`;

export const Container = styled.aside`
  height: 100%;
  position: relative;
`;

export const CloseButton = styled(KitCloseButton)`
  position: absolute;
  right: 7px;
  top: 7px;
  z-index: 2;
`;

export const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
  max-height: 100%;
`;

interface HeadingProps {
  $isBordered?: boolean;
}

export const Heading = styled.div<HeadingProps>`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 18px;
  font-weight: 400;

  ${({ theme }) =>
    theme.range(
      "padding",
      "15px",
      "22px",
      `${theme.breakpoints.lg}px`,
      `${theme.breakpoints.xl}px`,
    )};

  ${({ $isBordered, theme }) =>
    $isBordered && `border-bottom: 1px solid ${theme.divider}`};
`;

export const HeadingIcon = styled(Icon)`
  transition: background-color 0.3s;
  background-color: ${({ theme }) => theme.iconDefault};

  &:hover {
    background-color: ${({ theme }) => color(theme.iconDefault).darken(0.2).hex()};
  }
`;

interface ContentProps {
  disabled?: boolean;
  className?: string;
}

export const Content = styled(StyledScrollBars)<ContentProps>`
  animation: 0.3s ${keyframes`${fadeIn}`};

  & > div:first-child {
    padding: 15px;
  }

  ${(props) =>
    props.disabled &&
    `
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    `}
`;

export const PANEL_TRANSITION_MS = 150;

interface TransitionWrapperProps {
  $isTransitioning: boolean;
}

export const TransitionWrapper = styled.div<TransitionWrapperProps>`
  height: 100%;
  opacity: ${({ $isTransitioning }) => ($isTransitioning ? 0 : 1)};
  transition: opacity ${PANEL_TRANSITION_MS}ms ease-in-out;
`;

// Every drawer's footer. Its buttons sit right-aligned, as in modals and
// settings cards, so the primary is always in the same corner; when a narrow
// drawer wraps them, the primary goes on top. A footer outside the drawer's
// padded Content adds the side padding itself.
export const DrawerFooter = styled(ButtonRow).attrs({ $align: "end" })`
  width: 100%;
  display: ${(props) => (props.hidden ? "none" : "flex")};
  flex-wrap: wrap-reverse;
  padding: 20px 0;
`;
